import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useStateMachine } from "little-state-machine";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import testIds from "constants/testIds";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import usePermissions from "hooks/usePermissions";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import Cookies from "js-cookie";
import Badge from "components/v2/Badge/Badge";

const SHIPMENT_STATUS = ["Delayed", "In transit", "Incomplete", "Delivered", "Auto-closed", "Booked", "Cancelled"];

function NavPills({ isQuickRequest, isLoading, numberOfQuotes }) {
  const { state } = useStateMachine();
  const [activeKey, setActiveKey] = useState();
  const [disablePills, setDisablePills] = useState(false);
  const [disableBilling, setDisableBilling] = useState(true);
  const navigate = useNavigate();
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { permissions: permissionData, user_type, payment_location } = JSON.parse(permissions || "{}");
  const [isCustomBroker] = useCustomBrokerPermission(isQuickRequest);
  const id = useParams().id.toUpperCase();
  const shipmentId = id || state.shipmentId;
  const { data: shipmentData, error: shipmentBasicError } = useGetShipmentBasic(shipmentId, {
    enabled: !isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });
  const isOffPlatform = shipmentData?.shipment?.is_off_platform_shipment;
  // const isQuotationAvailable = shipmentData?.shipment?.quotes_received === 0; // WEB-8982: This wasn't on production, but on develop
  const { data: quickDetailsData } = useGetQuickRequestDetails(shipmentId, {
    enabled: isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });
  const customBrokerWasInvited = shipmentData?.shipment?.is_customs_broker_invited_provider || false;
  const isParentShipment = shipmentData?.related_shipments?.some((item) => item?.relation?.toLowerCase() === "child");
  const { data: shipmentDetails = {}, error: shipmentError } = useGetShipmentDetails(
    id,
    Cookies.get("is_guest") !== "true", // Don't update the notification setting if the user is a guest
    {
      enabled: !!id && SHIPMENT_STATUS.includes(shipmentData?.shipment?.request_status) && !isParentShipment,
    },
  );
  const paymentLocationReference = isQuickRequest
    ? quickDetailsData?.organization?.payment_location_reference
    : shipmentData?.organization?.payment_location_reference;
  const hasLocationAccess =
    payment_location?.locations.some((item) => item.value === paymentLocationReference) ||
    user_type === "provider" ||
    shipmentDetails?.viewing_as_guest_user;

  const isGuestUser = Cookies.get("is_guest") === "true" || shipmentDetails?.viewing_as_guest_user;

  const shipmentStatus = shipmentData?.shipment?.request_status;

  const handleNavChange = (eventKey) => {
    navigate(eventKey);
  };

  useEffect(() => {
    if (!shipmentId) return;
    let isMounted = true;
    const match = shipmentId.match(/\d+/g);

    if (match && isMounted && shipmentData && !isQuickRequest) {
      handleShowPills();
    }
    if (isQuickRequest) {
      setActiveKey(window.location.pathname);
    }
    return () => {
      isMounted = false;
    };
  }, [shipmentId, shipmentData]);

  // Handle whether or not the Tracking tab is disabled
  const handleShowPills = () => {
    if (!shipmentData) return;
    // If the shipment status is not in the list of options, then the Tracking tab is disabled
    if (SHIPMENT_STATUS.includes(shipmentStatus)) setDisablePills(false);
    else setDisablePills(true);

    if (
      (shipmentData?.shipment?.invoices > 0 || shipmentData?.shipment?.invoices?.length > 0) &&
      shipmentData?.settings?.invoice_audit_type !== 5 && // Only show the billing tab if the invoice audit type is not 5 (no invoicing)
      permissionData?.invoicing?.view_invoices // Only show the billing tab if the user has permission to view invoices
    ) {
      setDisableBilling(false);
    } else {
      setDisableBilling(true);
    }

    setActiveKey(window.location.pathname);
  };

  useEffect(() => {
    if (
      (shipmentData?.shipment?.invoices > 0 || shipmentData?.shipment?.invoices?.length > 0) &&
      shipmentData?.settings?.invoice_audit_type !== 5 && // Only show the billing tab if the invoice audit type is not 5 (no invoicing)
      permissionData?.invoicing?.view_invoices // Only show the billing tab if the user has permission to view invoices
    ) {
      setDisableBilling(false);
    } else {
      setDisableBilling(true);
    }
  }, [shipmentData]);

  useEffect(() => {
    setActiveKey(document.location.pathname);
  }, [document.location.pathname]);

  const isTrackingDisabled = () => {
    if (isLoading) {
      return true;
    }
    if (Cookies.get("is_guest") === "true") return false;
    if (!hasLocationAccess) return true;
    if (isQuickRequest) {
      // if related request of this quick request is a shipment enable tracking page
      if (SHIPMENT_STATUS.includes(quickDetailsData?.related_request?.related_request_status)) {
        return false;
      }
      return true;
    }
    if (isParentShipment) return true;
    if (shipmentBasicError?.response?.status === 403) return true;
    if (shipmentError?.response?.status === 403) return true;
    return disablePills;
  };

  let pills = [
    {
      title: isQuickRequest ? (
        intl.formatMessage({
          id: "dashboard.widgets.groupName.quickRequest",
          defaultMessage: "Quick Request",
        })
      ) : (
        <div className="d-flex flex-nowrap align-items-center">
          {intl.formatMessage({
            id: "general.quotes",
            defaultMessage: "Quotes",
          })}
          {numberOfQuotes >= 0 && !isGuestUser && !isCustomBroker && !isOffPlatform && (
            <span className="ml-md d-inline-flex align-items-center">
              <Badge label={numberOfQuotes} color="brand" type="category-badge" />
            </span>
          )}
        </div>
      ),
      eventKey: `${isQuickRequest ? `/shipment/${shipmentId}/quick` : `/shipment/${shipmentId}`}`,
      testId: testIds.SHIPMENT_DASHBOARD_QUOTES_TAB,
      disabled:
        isLoading ||
        isOffPlatform ||
        !hasLocationAccess ||
        (!isQuickRequest && !permissionData?.quoting?.view_quote_requests) ||
        (isCustomBroker && !customBrokerWasInvited),
      // (isQuotationAvailable && isOffPlatform), // WEB-8982: This wasn't on production, but on develop
    },

    (!isQuickRequest || (isQuickRequest && permissionData?.tracking?.view_shipments)) && {
      title: intl.formatMessage({
        id: "general.tracking",
        defaultMessage: "Tracking",
      }),
      eventKey: isQuickRequest ? `/shipment/${shipmentId}/quick/tracking` : `/shipment/${shipmentId}/tracking`,
      testId: testIds.SHIPMENT_DASHBOARD_TRACKING_TAB,
      disabled: isTrackingDisabled(),
    },
    !isQuickRequest && {
      title: intl.formatMessage({
        id: "components.molecules.top-nav.billing",
        defaultMessage: "Billing",
      }),
      eventKey: `/shipment/${shipmentId}/billing`,
      disabled: isLoading ? true : isQuickRequest ? true : disableBilling,
      testId: testIds.SHIPMENT_DASHBOARD_BILLING_TAB,
    },
    ...(!isQuickRequest && user_type === "shipper" && JSON.parse(permissions).permissions.admin_settings.view_audit_trail
      ? [
          {
            title: intl.formatMessage({
              id: "billing.audit",
              defaultMessage: "Audit",
            }),
            eventKey: `${
              isQuickRequest ? `/shipment/${shipmentId}/quick/audit-trail` : `/shipment/${shipmentId}/audit-trail`
            }`,
            testId: testIds.SHIPMENT_DASHBOARD_AUDIT_TRAIL_TAB,
            disabled: isLoading || !hasLocationAccess,
          },
        ]
      : []),
    {
      title: intl.formatMessage({
        id: "general.details",
        defaultMessage: "Details",
      }),
      eventKey: `${isQuickRequest ? `/shipment/${shipmentId}/quick/detail` : `/shipment/${shipmentId}/detail`}`,
      testId: testIds.SHIPMENT_DASHBOARD_DETAILS_TAB,
      disabled:
        isLoading ||
        !hasLocationAccess ||
        (!isQuickRequest &&
          !permissionData?.quoting?.view_quote_requests &&
          !permissionData?.quoting?.view_quick_requests &&
          Cookies.get("is_guest") !== "true"), // If user is a guest, they should be able to see the shipment details
    },
  ];

  // Filter pills to keep only "Shipment Details" and "Tracking" if is_guest is true
  if (Cookies.get("is_guest") === "true" || shipmentDetails?.viewing_as_guest_user) {
    const allowedTitles = [
      intl.formatMessage({
        id: "general.tracking",
        defaultMessage: "Tracking",
      }),
      intl.formatMessage({
        id: "general.shipmentDetails",
        defaultMessage: "Shipment Details",
      }),
      intl.formatMessage({
        id: "general.details",
        defaultMessage: "Details",
      }),
    ];
    pills = pills.filter((pill) => pill && allowedTitles.includes(pill.title));
  }

  return (
    <div className="d-block">
      <PillTabs
        id="quote-statuses"
        activeKey={activeKey}
        onSelect={handleNavChange}
        tabItems={pills}
        customActive
        isResponsive={false}
      />
    </div>
  );
}

NavPills.propTypes = {
  isQuickRequest: PropTypes.bool,
  isLoading: PropTypes.bool,
  numberOfQuotes: PropTypes.number,
};

NavPills.defaultProps = {
  isQuickRequest: false,
  isLoading: false,
  numberOfQuotes: undefined,
};

export default NavPills;
